// v3.15-1200 COMP-006 — CEO Payroll (Pending → Paid, Batches, Employee Totals).
//
// Payroll Engine reads the Compensation Ledger. It DOES NOT calculate
// compensation and NEVER modifies Compensation Events.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, DollarSign, Users, Layers, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const nextFriday = () => {
  const d = new Date();
  const day = d.getDay();
  const delta = (5 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};

const Tile = ({ label, value, icon: Icon, accent = 'blue', testId }) => (
  <div
    data-testid={testId}
    className={
      'rounded-2xl bg-white p-5 shadow-sm border-2 flex items-center gap-4 ' +
      (accent === 'emerald' ? 'border-emerald-100' :
        accent === 'amber' ? 'border-amber-100' :
        accent === 'violet' ? 'border-violet-100' :
        'border-blue-100')
    }
  >
    <div className={
      'w-11 h-11 rounded-xl flex items-center justify-center ' +
      (accent === 'emerald' ? 'bg-emerald-50 text-emerald-600' :
        accent === 'amber' ? 'bg-amber-50 text-amber-600' :
        accent === 'violet' ? 'bg-violet-50 text-violet-600' :
        'bg-blue-50 text-blue-600')
    }>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={
        'text-2xl font-extrabold ' +
        (accent === 'emerald' ? 'text-emerald-700' :
          accent === 'amber' ? 'text-amber-700' :
          accent === 'violet' ? 'text-violet-700' :
          'text-blue-700')
      }>{value}</div>
    </div>
  </div>
);

const RunPayrollModal = ({ open, onClose, onRun, pool }) => {
  const [payrollDate, setPayrollDate] = useState(nextFriday());
  const [notes, setNotes]             = useState('');
  const [saving, setSaving]           = useState(false);

  const handleRun = async () => {
    if (!payrollDate) { toast.error('Payroll date is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/payroll/batches', {
        payroll_date: payrollDate,
        notes:        notes || undefined,
      });
      toast.success(`Batch ${data?.batch?.batch_id} · ${data?.batch?.entry_count} entries · ${fmtMoney(data?.batch?.total_amount)}`);
      onRun?.(data?.batch);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Payroll run failed';
      if (detail === 'nothing_to_pay') toast.error('Nothing to pay. Pending pool is empty.');
      else toast.error(detail);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-1">Run Friday Payroll</h2>
          <p className="text-sm text-slate-500 mb-4">
            Payroll flips every pending ledger entry to <span className="font-semibold text-emerald-700">paid</span>.
            Compensation Events remain immutable.
          </p>
          <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
            <div>Employees pending: <b>{pool?.employee_count || 0}</b></div>
            <div>Entries pending: <b>{pool?.entry_count || 0}</b></div>
            <div>Total pending: <b className="text-emerald-700">{fmtMoney(pool?.total_amount || 0)}</b></div>
          </div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Payroll date (Friday)</label>
          <Input
            data-testid="input-payroll-date"
            type="date" value={payrollDate}
            onChange={(e) => setPayrollDate(e.target.value)}
            className="mb-3"
          />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes (optional)</label>
          <Input
            data-testid="input-payroll-notes"
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Payroll cycle notes"
          />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" data-testid="cancel-payroll-btn" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              data-testid="confirm-payroll-btn"
              onClick={handleRun} disabled={saving || !pool?.entry_count}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay {fmtMoney(pool?.total_amount || 0)}</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function PayrollEnginePage() {
  const [tab, setTab] = useState('pending'); // pending | batches
  const [pool, setPool]         = useState({ employees: [], employee_count: 0, entry_count: 0, total_amount: 0 });
  const [batches, setBatches]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showRun, setShowRun]   = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [poolRes, batchRes] = await Promise.all([
        api.get('/payroll/pending'),
        api.get('/payroll/batches', { params: { limit: 200 } }),
      ]);
      setPool(poolRes?.data || {});
      setBatches(batchRes?.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) setError('Payroll administration is restricted to admins/directors.');
      else toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totals = useMemo(() => {
    let paidAll = 0, batchCount = batches.length;
    for (const b of batches) paidAll += Number(b.total_amount || 0);
    return { paidAll, batchCount };
  }, [batches]);

  return (
    <div className="space-y-4" data-testid="payroll-engine-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <DollarSign className="w-6 h-6 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Payroll Engine</h1>
            <p className="text-sm text-slate-500">
              Friday payroll — flips pending Compensation Ledger entries to paid.
              Never calculates compensation.
            </p>
          </div>
        </div>
        <Button
          data-testid="open-run-payroll-btn"
          onClick={() => setShowRun(true)}
          disabled={!pool.entry_count}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          Run Payroll
        </Button>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl"
             data-testid="payroll-forbidden">{error}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Tile testId="tile-pending-total"  label="Pending Total"   value={fmtMoney(pool.total_amount || 0)} icon={DollarSign} accent="amber" />
            <Tile testId="tile-pending-emp"    label="Pending Employees" value={pool.employee_count || 0}       icon={Users}      accent="blue" />
            <Tile testId="tile-batches-count"  label="Total Batches"   value={totals.batchCount}                icon={Layers}     accent="violet" />
            <Tile testId="tile-batches-total"  label="Total Paid (all time)" value={fmtMoney(totals.paidAll)}   icon={CheckCircle2} accent="emerald" />
          </div>

          <div className="flex items-center gap-1 border-b border-slate-200">
            {['pending', 'batches'].map((t) => (
              <button
                key={t}
                data-testid={`tab-${t}`}
                onClick={() => setTab(t)}
                className={
                  'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ' +
                  (tab === t
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800')
                }
              >{t === 'pending' ? 'Pending Pool' : 'Batch History'}</button>
            ))}
          </div>

          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : tab === 'pending' ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {(pool.employees || []).length === 0 ? (
                <div className="p-10 text-center text-slate-500" data-testid="pending-empty">
                  No pending compensation to pay. All caught up.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="pending-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Employee</th>
                      <th className="text-left px-4 py-2">Company</th>
                      <th className="text-right px-4 py-2">Entries</th>
                      <th className="text-right px-4 py-2">Total Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pool.employees.map((e) => (
                      <tr key={e.employee_id}
                          data-testid={`pending-row-${e.employee_id}`}
                          className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-2 font-semibold text-slate-800">{e.employee_name || e.employee_id}</td>
                        <td className="px-4 py-2">{e.company_name || '—'}</td>
                        <td className="px-4 py-2 text-right">{e.entry_count}</td>
                        <td className="px-4 py-2 text-right font-semibold text-amber-700">{fmtMoney(e.total_pending)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
              {batches.length === 0 ? (
                <div className="p-10 text-center text-slate-500" data-testid="batches-empty">
                  No payroll batches have been run yet.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="batches-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Batch</th>
                      <th className="text-left px-4 py-2">Payroll Date</th>
                      <th className="text-left px-4 py-2">Processing Date</th>
                      <th className="text-right px-4 py-2">Employees</th>
                      <th className="text-right px-4 py-2">Entries</th>
                      <th className="text-right px-4 py-2">Total</th>
                      <th className="text-center px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.id}
                          data-testid={`batch-row-${b.id}`}
                          className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-2 font-mono text-xs">{b.batch_id}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{fmtDate(b.payroll_date)}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{fmtDate(b.processing_date)}</td>
                        <td className="px-4 py-2 text-right">{b.employee_count}</td>
                        <td className="px-4 py-2 text-right">{b.entry_count}</td>
                        <td className="px-4 py-2 text-right font-semibold text-emerald-700">{fmtMoney(b.total_amount)}</td>
                        <td className="px-4 py-2 text-center">
                          <span data-testid={`batch-status-${b.status}`}
                                className={
                                  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ' +
                                  (b.status === 'completed'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200')
                                }>{b.status}</span>
                        </td>
                        <td className="px-4 py-2 text-xs">{b.created_by || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      <RunPayrollModal
        open={showRun}
        onClose={() => setShowRun(false)}
        onRun={() => fetchAll()}
        pool={pool}
      />
    </div>
  );
}
